import { useEffect, useRef } from 'react'

interface TurnstileWidgetProps {
  onVerify: (token: string) => void
  onError?: () => void
  className?: string
}

declare global {
  interface Window {
    turnstile: {
      render: (element: string | HTMLElement, config: TurnstileConfig) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
  }
}

interface TurnstileConfig {
  sitekey: string
  callback: (token: string) => void
  'error-callback'?: (error?: string) => void
  theme?: 'light' | 'dark' | 'auto'
  size?: 'normal' | 'compact'
}

export function TurnstileWidget({ onVerify, onError, className = '' }: TurnstileWidgetProps) {
  const widgetRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const isRenderedRef = useRef(false)
  const onVerifyRef = useRef(onVerify)
  const onErrorRef = useRef(onError)
  const checkTurnstileIntervalRef = useRef<number | null>(null)

  // Keep callback refs up to date
  useEffect(() => {
    onVerifyRef.current = onVerify
    onErrorRef.current = onError
  }, [onVerify, onError])

  // Determine if we're in development mode
  const isDevelopment =
    import.meta.env.DEV || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

  // Use test key for development (always passes), production key otherwise
  // Cloudflare test keys: 1x00000000000000000000AA (always passes)
  const TURNSTILE_SITE_KEY = isDevelopment
    ? '1x00000000000000000000AA' // Test key - always passes in development
    : import.meta.env.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAAB_jX-fvQNQXN6I3lFoO3YR1M3w'

  useEffect(() => {
    if (!widgetRef.current) return

    // Don't render if already rendered
    if (isRenderedRef.current && widgetIdRef.current) {
      return
    }

    // Load Turnstile script if not already loaded
    if (!window.turnstile) {
      const script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.defer = true
      script.onerror = () => {
        console.error('Failed to load Turnstile script')
        onErrorRef.current?.()
      }
      document.head.appendChild(script)
    }

    // Wait for script to load, then render widget
    const renderWidget = () => {
      if (!widgetRef.current) return

      // Check if widget already exists in this container
      if (widgetIdRef.current && window.turnstile) {
        // Widget already rendered, don't render again
        return
      }

      // Clear container before rendering
      if (widgetRef.current) {
        widgetRef.current.innerHTML = ''
      }

      if (window.turnstile && widgetRef.current) {
        try {
          widgetIdRef.current = window.turnstile.render(widgetRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            callback: (token: string) => {
              onVerifyRef.current(token)
            },
            'error-callback': (error?: string) => {
              console.error('Turnstile error:', error)
              onErrorRef.current?.()
            },
            theme: 'auto',
            size: 'normal',
          })
          isRenderedRef.current = true
        } catch (error) {
          console.error('Failed to render Turnstile widget:', error)
          onErrorRef.current?.()
        }
      }
    }

    if (window.turnstile) {
      renderWidget()
    } else {
      // Wait for script to load (with timeout)
      let attempts = 0
      const maxAttempts = 50 // 5 seconds max wait
      checkTurnstileIntervalRef.current = window.setInterval(() => {
        attempts++
        if (window.turnstile) {
          if (checkTurnstileIntervalRef.current) {
            clearInterval(checkTurnstileIntervalRef.current)
            checkTurnstileIntervalRef.current = null
          }
          renderWidget()
        } else if (attempts >= maxAttempts) {
          if (checkTurnstileIntervalRef.current) {
            clearInterval(checkTurnstileIntervalRef.current)
            checkTurnstileIntervalRef.current = null
          }
          console.error('Turnstile script failed to load after timeout')
          onErrorRef.current?.()
        }
      }, 100)
    }

    // Cleanup
    return () => {
      // Clear interval if still running
      if (checkTurnstileIntervalRef.current) {
        clearInterval(checkTurnstileIntervalRef.current)
        checkTurnstileIntervalRef.current = null
      }

      // Remove widget
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current)
        } catch (error) {
          // Ignore errors during cleanup
        }
        widgetIdRef.current = null
        isRenderedRef.current = false
      }

      // Clear container
      if (widgetRef.current) {
        widgetRef.current.innerHTML = ''
      }
    }
  }, [TURNSTILE_SITE_KEY]) // Removed onVerify and onError from dependencies - using refs instead

  return (
    <div className={`flex justify-center ${className}`}>
      <div ref={widgetRef} />
    </div>
  )
}
